import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './menu.css'; // Import the CSS file

// --- ASSET AND COMPONENT IMPORTS ---
// Assuming all components are in src/components and assets in src/assets
// FIX: Changed '../../' to '../' to fix the 'outside of src/' import error.
import FloatingChatBot from '../components/FloatingChatBot';
import ContactSection from '../components/ContactSection';  
import SimpleBanner from '../components/SimpleBanner';      
import aboutBg from '../assets/About.jpg';                   

// ----------------------------------------------------
// Configuration and API Endpoint
// ----------------------------------------------------
// ⭐️ IMPORTANT: Update this URL if your backend server is running on a different port or domain.
const API_URL = 'https://admin.theporch.in/api'; 

// ----------------------------------------------------
// Component Definitions
// ----------------------------------------------------

// MenuItemCard component: Displays a single menu item (Handles Base64 image string from DB)
const MenuItemCard = ({ title, description, price, imageSrc }) => {
  // Use a placeholder if imageSrc is missing or not a valid Base64 string
  const finalImageSrc = imageSrc && imageSrc.startsWith('data:image') 
    ? imageSrc 
    : 'https://placehold.co/100x100/A0522D/ffffff?text=No+Image';

  return (
    <div className="menu-item-card">
      <div className="menu-item-image-container">
        {/* imageSrc is the Base64 string from the server (item.image_url) */}
        <img src={finalImageSrc} alt={title} className="menu-item-image" />
      </div>
      <div className="menu-item-details">
        <h4 className="menu-item-title">{title}</h4>
        <p className="menu-item-description">{description}</p>
      </div>
      {/* Ensure price is parsed as a float and displayed correctly */}
      <div className="menu-item-price">Rs. {parseFloat(price).toFixed(2)}</div>
    </div>
  );
};

// MenuItemSection component: Renders a grid of MenuItemCards and a "View More" button
const MenuItemSection = ({ items, isLoading }) => {
  const [initialVisibleCount] = useState(8); 
  const [showAll, setShowAll] = useState(false); 

  useEffect(() => {
    // Reset visibility when the items (category) changes
    setShowAll(false); 
  }, [items]);

  const handleToggleView = () => {
    setShowAll(prevState => !prevState); 
  };

  if (isLoading) {
    return (
      <div className="loading-state-message text-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading menu items...</p>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="no-items-message text-center p-8">
        <p className="text-gray-500">No items found in this category.</p>
      </div>
    );
  }

  const itemsToDisplay = showAll ? items : items.slice(0, initialVisibleCount);

  return (
    <div className="menu-item-section">
      <div className="menu-items-grid">
        {itemsToDisplay.map((item) => (
          <MenuItemCard
            key={item.id} // Use unique item ID as key
            title={item.title}
            description={item.description}
            price={item.price}
            imageSrc={item.image_url} // Base64 string from server
          />
        ))}
      </div>
      {/* Show "View More/Hide" button only if there are more items than initially displayed */}
      {items.length > initialVisibleCount && (
        <button className="view-more-button" onClick={handleToggleView}>
          {showAll ? 'Hide' : 'View More'}
        </button>
      )}
    </div>
  );
};

// MenuCategoryList component: Displays the list of menu categories for navigation
const MenuCategoryList = ({ activeCategory, setActiveCategory, categories }) => {
  // Extract category names from the fetched category objects
  const categoryNames = categories.map(cat => cat.name);

  return (
    <div className="menu-category-list">
      <div className="menu-category-item menu-category-header">MENU & PRICING</div>
      {categoryNames.map((category) => (
        <div
          key={category}
          className={`menu-category-item ${activeCategory === category ? 'active' : ''}`}
          onClick={() => setActiveCategory(category)}
        >
          {category}
        </div>
      ))}
    </div>
  );
};

// SpecialMenuSection component: Highlights special menu categories with images
const SpecialMenuSection = ({ setActiveCategory, categories }) => {
  // FIX APPLIED HERE: Use ALL fetched categories instead of limiting to the first 4
  // If you only want the first 4, revert to: const specialCategories = categories.slice(0, 4);
  const specialCategories = categories; 

  const handleCategoryClick = (categoryName) => {
    setActiveCategory(categoryName);
  };

  return (
    <>
      <Helmet>
        <title>Menu | The Porch Cafe – Veg Restaurant Kota</title>
        <meta
          name="description"
          content="Explore the diverse and flavorful menu at The Porch Cafe – a rooftop vegetarian restaurant in Kota offering North Indian, Chinese, and more."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://admin.theporch.in/menu" />

        {/* Open Graph / Facebook and Twitter tags remain unchanged */}
        <meta property="og:title" content="Menu | The Porch Cafe – Veg Restaurant Kota" />
        <meta
          property="og:description"
          content="Check out our appetizing range of vegetarian dishes from Indian to Italian cuisine at The Porch Cafe."
        />
        <meta property="og:image" content="https://admin.theporch.in/logo.png" />
        <meta property="og:url" content="https://admin.theporch.in/menu" />
        <meta property="og:type" content="restaurant.menu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Menu | The Porch Cafe – Veg Restaurant Kota" />
        <meta
          name="twitter:description"
          content="Savor the best vegetarian menu in Kota, served on our cozy rooftop setting."
        />
        <meta name="twitter:image" content="https://admin.theporch.in/logo.png" />

        {/* Structured Data remains unchanged */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Restaurant",
              "name": "The Porch Cafe & Restaurant",
              "image": "https://admin.theporch.in/logo.png",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Hotel The Pride, Talwandi",
                "addressLocality": "Kota",
                "addressRegion": "Rajasthan",
                "postalCode": "324005"
              },
              "telephone": "+91 9024283004",
              "servesCuisine": ["North Indian", "Chinese", "Italian", "Vegetarian", "Fast Food"],
              "url": "https://admin.theporch.in/menu",
              "menu": "https://admin.theporch.in/menu",
              "description": "Discover the curated vegetarian menu of The Porch Cafe featuring locally inspired flavors and rooftop ambiance.",
              "sameAs": [
                "https://www.instagram.com/_the.porch/",
                "https://www.facebook.com/people/The-Porch-Cafe-Restaurant/100083751721732/",
                "https://www.swiggy.com/city/kota/the-porch-cafe-and-restaurant-hotel-the-pride-talwandi-rest888157",
                "https://www.zomato.com/kota/the-porch-cafe-restaurant-talwandi"
              ]
            }
          `}
        </script>
      </Helmet>
    <section className="special-menu-section">
      <h4>TASTE THE BEST THAT SURPRISE YOU</h4>
      <h2>OUR SPECIAL MENU</h2>
      <p>
        Enjoy the unique dishes from the basillico restaurant that only our restaurant has. Fusce malesuada, lorem vitae euismod lobortis.
      </p>

      <div className="category-grid">
        {specialCategories.map((category) => (
          <div
            className="category-item"
            key={category.id} // Use unique category ID as key (assuming DB provides one)
            onClick={() => handleCategoryClick(category.name)}
          >
            <div className="category-image-container">
              {/* Use Base64 image string directly from the fetched category object (category.image is the Base64 string from the server) */}
              <img src={category.image} alt={category.name} className="category-image" />
            </div>
            <span className="category-title">{category.name}</span>
          </div>
        ))}
      </div>
    </section>
    </>
  );
};

// MenuHeaderSection component: Displays a simple banner for the menu page
const MenuHeaderSection = () => {
  return (
    <SimpleBanner bgImage={aboutBg} headingText="OUR Menu" />
  );
};

// Main Menu component
const Menu = () => {
  const location = useLocation();
  const menuPricingRef = useRef(null); 

  // --- State for API Data ---
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // Will be set after data fetch
  const [isLoading, setIsLoading] = useState(true);
  // Changed error state to hold an object or string for better debugging
  const [error, setError] = useState(null); 

  // --- Data Fetching Logic ---
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    let fetchedCategories = [];
    let fetchedItems = [];

    try {
      // 1. Fetch Categories
      const categoriesResponse = await fetch(`${API_URL}/categories`);
      if (!categoriesResponse.ok) throw new Error(`Category fetch failed: ${categoriesResponse.statusText}`);
      fetchedCategories = await categoriesResponse.json();
      
      // 2. Fetch Menu Items
      const itemsResponse = await fetch(`${API_URL}/menu_items`);
      if (!itemsResponse.ok) throw new Error(`Menu item fetch failed: ${itemsResponse.statusText}`);
      fetchedItems = await itemsResponse.json();
      
      setCategories(fetchedCategories);
      setMenuItems(fetchedItems);
      
      console.log('Fetched Categories:', fetchedCategories);
      console.log('Fetched Menu Items:', fetchedItems);

      // Set the initial active category only if categories are present
      if (fetchedCategories.length > 0) {
        setActiveCategory(fetchedCategories[0].name);
        // Also check if the first category has items associated with it
        const hasItems = fetchedItems.some(item => item.category === fetchedCategories[0].name);
        if (!hasItems) {
            console.warn(`No menu items found for the initial category: ${fetchedCategories[0].name}`);
        }
      } else {
        console.warn("No categories were returned from the API.");
        setError("Menu data loaded successfully, but no categories were found in the database. Please check your categories table.");
      }
    } catch (err) {
      console.error("API Fetch Error:", err);
      // Set a more detailed error message based on the fetch failure
      setError(`Network error or CORS issue. Please ensure the backend server is running and accessible at ${API_URL}. Details: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Scrolling and Category Change Logic ---
  useEffect(() => {
    // If the data is ready and a specific category was requested via state
    if (!isLoading && categories.length > 0 && location.state && location.state.scrollToCategory) {
      const categoryToScrollTo = location.state.scrollToCategory;
      // Check if the requested category actually exists in the fetched data
      const categoryExists = categories.some(cat => cat.name === categoryToScrollTo);
      
      if (categoryToScrollTo && categoryExists) {
        setActiveCategory(categoryToScrollTo);
        const timer = setTimeout(() => {
          if (menuPricingRef.current) {
            menuPricingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100); 
        return () => clearTimeout(timer);
      }
    }
  }, [location.state, isLoading, categories]);

  // Filter menu items based on the active category
  // This depends on activeCategory being non-null, which is handled in fetchData
  const filteredMenuItems = menuItems.filter(item => item.category === activeCategory);

  return (
    <>
      <MenuHeaderSection />
      
      {/* Display error message if the fetch failed or if data is missing */}
      {error && (
        <div className="error-message-container text-center p-8 bg-red-100 border border-red-400 text-red-700 rounded mx-4 my-8">
          <h3>Error loading menu</h3>
          <p className="text-sm font-medium mt-2">{error}</p>
          <p className="text-xs mt-4">
            **Action Required:** Please verify the Node.js server is running and check the console for detailed error messages (like CORS failure).
          </p>
        </div>
      )}

      {/* Show special menu section if data is loaded and categories exist */}
      {!isLoading && !error && categories.length > 0 && (
        <SpecialMenuSection 
          setActiveCategory={setActiveCategory} 
          categories={categories} 
        />
      )}

      <div className="menu-page-container" ref={menuPricingRef}>
        <div className="menu-sidebar">
          {/* Menu Category List (Show loading state or component) */}
          {isLoading && !error ? (
            <div className="menu-category-list">
              <div className="menu-category-item menu-category-header">MENU & PRICING</div>
              <div className="text-center p-4 text-gray-400">Loading...</div>
            </div>
          ) : (
            <MenuCategoryList 
              activeCategory={activeCategory} 
              setActiveCategory={setActiveCategory} 
              categories={categories}
            />
          )}
        </div>

        <div className="menu-content">
          {/* Menu Items for the Active Category (Render only when a category is active) */}
          {activeCategory && (
            <MenuItemSection 
              items={filteredMenuItems} 
              isLoading={isLoading}
            />
          )}
          
          <FloatingChatBot />
        </div>
      </div>
      <ContactSection/>
    </>
  );
};

export default Menu;